import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, User } from "lucide-react";
import { api } from "@/lib/api";

/**
 * CollaboratorSearch — Iter-124
 *
 * Shown in the Timesheet header for CEO/ADMIN (and any role that can view other
 * users' timesheets). Types a name/email → debounced fetch of matching users → click
 * a result to navigate to `/timesheet?user_id=<uid>`.
 *
 * Keeps the list of collaborators local (no extra API — uses the existing
 * GET /users endpoint which is already loaded for CEO/ADMIN/MANAGER/GESTIONNAIRE).
 */
export default function CollaboratorSearch({ currentUserId }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Close dropdown on outside click.
  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Lazy-load users list on first focus.
  const ensureLoaded = async () => {
    if (loaded) return;
    try {
      const r = await api.get("/users");
      // Exclude the current user (self-timesheet already accessible) + inactive accounts.
      const list = (r.data || [])
        .filter((u) => u.user_id !== currentUserId && u.is_active !== false)
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setUsers(list);
      setLoaded(true);
    } catch (_e) {
      // Silent — the CEO will see 0 results if unauthorized.
    }
  };

  const qNorm = q.trim().toLowerCase();
  const filtered = qNorm === ""
    ? users.slice(0, 12)
    : users.filter((u) =>
        (u.name || "").toLowerCase().includes(qNorm) ||
        (u.email || "").toLowerCase().includes(qNorm),
      ).slice(0, 12);

  return (
    <div ref={wrapRef} className="relative" data-testid="timesheet-collab-search">
      <div className={`flex items-center gap-2 h-10 px-3 bg-white border rounded-md transition-colors ${
        open ? "border-brand-navy/50 shadow-sm" : "border-zinc-200"
      }`}>
        <Search size={14} className="text-zinc-400 shrink-0" />
        <input
          type="text"
          value={q}
          placeholder="Search collaborator…"
          onFocus={() => { ensureLoaded(); setOpen(true); }}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          data-testid="collab-search-input"
          className="bg-transparent outline-none text-sm flex-1 min-w-[180px] w-[200px]"
        />
        {q && (
          <button
            type="button"
            onClick={() => { setQ(""); setOpen(true); }}
            className="text-zinc-400 hover:text-zinc-700"
            aria-label="Clear"
          >
            <X size={13} />
          </button>
        )}
      </div>
      {open && (
        <div
          data-testid="collab-search-dropdown"
          className="absolute right-0 mt-1 w-[320px] max-w-[90vw] max-h-[360px] overflow-y-auto bg-white border border-zinc-200 rounded-md shadow-lg z-50"
        >
          {!loaded && (
            <div className="px-3 py-4 text-sm text-zinc-500 text-center">Loading collaborators…</div>
          )}
          {loaded && filtered.length === 0 && (
            <div className="px-3 py-4 text-sm text-zinc-500 text-center">No collaborator matches.</div>
          )}
          {loaded && filtered.map((u) => (
            <button
              key={u.user_id}
              type="button"
              onClick={() => {
                navigate(`/timesheet?user_id=${encodeURIComponent(u.user_id)}`);
                setOpen(false);
                setQ("");
              }}
              data-testid={`collab-search-pick-${u.user_id}`}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-zinc-50 border-b border-zinc-100 last:border-b-0"
            >
              <span className="w-7 h-7 rounded-full bg-brand-navy/10 text-brand-navy flex items-center justify-center shrink-0 text-[10px] font-semibold">
                {(u.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase() || <User size={12} />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-brand-ink truncate">{u.name || "—"}</div>
                <div className="text-[11px] text-zinc-500 font-mono truncate">{u.email}</div>
              </div>
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 font-mono shrink-0">
                {u.role}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
